import { useId, useState } from "react";
import { api } from "./api.js";
import { loadKey, signLogin } from "./kep.js";
import CertField from "./CertField.jsx";
import { useLook } from "./theme.jsx";

const KEY_EXT = ".dat,.jks,.pfx,.zs2,.sk";

// Вхід ключем КЕП: ключ відкривається в браузері, на сервер ідуть два підписи
// autoFocus — фокус на файл ключа, коли форму відкрили дією користувача
export default function KeyLogin({ onDone, expectEdrpou, autoFocus }) {
  const [keyFile, setKeyFile] = useState(null);
  const [certFiles, setCertFiles] = useState([]);
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [skin] = useLook("skin");
  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      setStatus("Відкриваю ключ…");
      const { box, edrpou, drfo } = await loadKey({ keyFile, password, certFiles, log: setStatus });
      if (expectEdrpou && edrpou !== expectEdrpou) throw new Error(`Це ключ іншого платника (${edrpou})`);
      setStatus("Підписую…");
      const ch = await api("/api/kep/challenge", { method: "POST" });
      const sigs = await signLogin(box, edrpou, ch.text);
      setStatus("Вхід у кабінет ДПС…");
      const r = await api("/api/dps/login", { method: "POST", body: { edrpou, drfo, nonce: ch.nonce, ...sigs } });
      setPassword("");
      setStatus(null);
      onDone(r);
    } catch (err) {
      setStatus(null);
      setError(err.message);
    }
  };
  const pickFile = (e) => setKeyFile(e.target.files[0] || null);
  // Вигляд minimal: зона «Перетягніть ключ» замість звичайного поля файлу — те саме поле, той самий стан і сабміт
  if (skin === "minimal") {
    return (
      <form className="card key-login" onSubmit={submit}>
        <p className="key-login-title">Вхід ключем КЕП</p>
        <p className="key-login-lead">Ключ підприємства або ФОП від будь-якого кваліфікованого надавача — той самий, яким ви підписуєте звітність.</p>
        <KeyDrop file={keyFile} onChange={pickFile} autoFocus={autoFocus} />
        <label className="field">Пароль ключа<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" required /></label>
        <CertField onChange={setCertFiles} />
        <p className="notice" aria-live="polite">{status}</p>
        <p className="alert" role="alert">{error}</p>
        <button type="submit" className="btn btn-block" disabled={!keyFile || Boolean(status)}>Увійти ключем</button>
        <p className="key-login-safe">
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4.5" y="9" width="11" height="8" rx="2" /><path d="M7 9V6.5a3 3 0 0 1 6 0V9" /></svg>
          <span>Ключ і пароль не передаються на сервер: підпис створюється у вашому браузері.</span>
        </p>
      </form>
    );
  }
  return (
    <form className="card card-elevated narrow" onSubmit={submit}>
      <p className="notice" style={{ marginBottom: 20 }}>Ключ і пароль не передаються на сервер: підпис створюється у вашому браузері.</p>
      <label className="field">Файл ключа<input type="file" accept={KEY_EXT} autoFocus={autoFocus} onChange={pickFile} required /></label>
      <label className="field">Пароль ключа<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" required /></label>
      <CertField onChange={setCertFiles} />
      <p className="notice" aria-live="polite">{status}</p>
      <p className="alert" role="alert">{error}</p>
      <button type="submit" className="btn btn-block" disabled={!keyFile || Boolean(status)}>Увійти ключем</button>
    </form>
  );
}

// Зона файлу ключа: прозоре поле файлу на всю зону — клік відкриває вибір файлу, перетягування кладе файл у те саме поле
// (браузер сам заповнює input і викликає change). Підпис кнопки — лише вигляд; доступна назва поля — «Файл ключа»
function KeyDrop({ file, onChange, autoFocus }) {
  const [drag, setDrag] = useState(false);
  const hint = useId();
  return (
    <div className={`key-drop${drag ? " drag" : ""}${file ? " has-file" : ""}`}
      onDragEnter={() => setDrag(true)} onDragLeave={() => setDrag(false)} onDrop={() => setDrag(false)}>
      <input className="key-drop-input" type="file" accept={KEY_EXT} autoFocus={autoFocus} onChange={onChange} required
        aria-label="Файл ключа" aria-describedby={hint} />
      <span className="key-drop-title" aria-hidden="true">{file ? "Ключ вибрано" : "Перетягніть ключ у цю область або"}</span>
      {file && <span className="key-drop-file">{file.name}</span>}
      <span className="btn key-drop-btn" aria-hidden="true">
        <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="7" cy="13" r="3.5" /><path d="m9.5 10.5 6-6M13.5 6.5l2 2M12 8l1.5 1.5" /></svg>
        {file ? "Вибрати інший файл" : "Завантажити з пристрою"}
      </span>
      <span className="key-drop-ext" id={hint}>Підтримуються розширення: .dat, .jks, .pfx, .zs2, .sk</span>
    </div>
  );
}
